import React from 'react';
import { Link2, FileText, History, LayoutDashboard, Moon, Sun, Award, Mail, User, LogIn, GitCompare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import veriLinkLogo from '@/assets/verilink-logo.png';

const navItems = [
  { id: 'link', label: 'Link Verification', icon: Link2 },
  { id: 'article', label: 'Article Check', icon: FileText },
  { id: 'email', label: 'Email Checker', icon: Mail },
  { id: 'compare', label: 'Article Compare', icon: GitCompare },
  { id: 'phishing-lab', label: 'Phishing Lab', icon: Award },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'history', label: 'History', icon: History },
];

export const Sidebar = ({ onShowAuth }) => {
  const { activeView, setActiveView, darkMode, toggleDarkMode, stats, earnedBadges } = useApp();
  const { isAuthenticated, user } = useAuth();

  return (
    <aside className="fixed left-0 top-0 h-full w-72 glass-card border-r border-border/50 flex flex-col z-40">
      <div className="p-6 border-b border-border/30">
        <div className="flex items-center gap-3">
          <img src={veriLinkLogo} alt="VeriLink" className="w-10 h-10 rounded-xl object-contain" />
          <div>
            <h1 className="font-display text-xl font-bold text-foreground">VeriLink</h1>
            <p className="text-xs text-muted-foreground">Trust verification</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeView === item.id;
          return (
            <button key={item.id} onClick={() => setActiveView(item.id)} data-testid={`nav-${item.id}`}
              className={cn("w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
                isActive ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-muted text-foreground")}>
              <Icon className={cn("w-5 h-5", isActive && "text-primary-foreground")} />
              <span className={cn("font-medium text-sm", isActive && "text-primary-foreground")}>{item.label}</span>
            </button>
          );
        })}

        {isAuthenticated && (
          <button onClick={() => setActiveView('account')} data-testid="nav-account"
            className={cn("w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200",
              activeView === 'account' ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-muted text-foreground")}>
            <User className={cn("w-5 h-5", activeView === 'account' && "text-primary-foreground")} />
            <span className={cn("font-medium text-sm", activeView === 'account' && "text-primary-foreground")}>My Account</span>
          </button>
        )}
      </nav>

      <div className="p-4 border-t border-border/30">
        <div className="glass rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Verified</span>
            <Badge variant="secondary" className="font-mono">{stats.totalVerifications}</Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Streak</span>
            <Badge variant="safe" className="font-mono">&#128293; {stats.streak}d</Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Badges</span>
            <Badge variant="glass" className="font-mono"><Award className="w-3 h-3 mr-1" />{earnedBadges.length}</Badge>
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-border/30">
        {isAuthenticated ? (
          <button onClick={() => setActiveView('account')} data-testid="sidebar-account-btn" className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors">
            {user?.picture ? (
              <img src={user.picture} alt={user?.name || user?.email} referrerPolicy="no-referrer"
                className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-border/40" data-testid="sidebar-avatar" />
            ) : (
              <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary-foreground">{(user?.name || user?.email || '?')[0].toUpperCase()}</span>
              </div>
            )}
            <div className="min-w-0 text-left">
              <p className="font-medium text-sm truncate" data-testid="sidebar-username">{user?.name || user?.email}</p>
              {user?.name && <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>}
            </div>
          </button>
        ) : (
          <Button variant="gradient" onClick={onShowAuth} className="w-full" data-testid="login-btn">
            <LogIn className="w-4 h-4 mr-2" />Sign In
          </Button>
        )}
      </div>

      <div className="p-4 border-t border-border/30">
        <Button variant="ghost" onClick={toggleDarkMode} className="w-full justify-start gap-3">
          {darkMode ? <Sun className="w-5 h-5 text-warning" /> : <Moon className="w-5 h-5 text-primary" />}
          <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
